/**
 * Discussion Items Component
 * Allows adding individual discussion points with attribution (employee vs manager)
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Plus, Trash2, User, Briefcase, Pencil } from 'lucide-react'
import type { DiscussionPoint } from '@/services/api/reviewService'

interface DiscussionItemsProps {
  points: DiscussionPoint[]
  onAddPoint: (content: string) => void
  onDeletePoint: (id: string) => void
  /** Change a point's wording; offered on your own points while canEdit holds
   *  (until the check-in is completed). */
  onUpdatePoint?: (id: string, content: string) => void
  currentUserRole: 'employee' | 'manager'
  canEdit: boolean
  placeholder?: string
  maxLength?: number
}

export function DiscussionItems({
  points,
  onAddPoint,
  onDeletePoint,
  onUpdatePoint,
  currentUserRole,
  canEdit,
  placeholder = 'Add a discussion point...',
  maxLength = 500,
}: DiscussionItemsProps) {
  const [content, setContent] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')

  const startEdit = (point: DiscussionPoint) => {
    setEditingId(point.id)
    setEditContent(point.content)
  }
  const saveEdit = (point: DiscussionPoint) => {
    const next = editContent.trim()
    if (next && next.length <= maxLength && next !== point.content) onUpdatePoint?.(point.id, next)
    setEditingId(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (content.trim() && content.length <= maxLength) {
      onAddPoint(content.trim())
      setContent('')
      setIsExpanded(false)
    }
  }

  const getTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)
    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  return (
    <div className="space-y-3">
      {/* Existing Points */}
      <AnimatePresence mode="popLayout">
        {points.length === 0 && !isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="rounded-lg border border-dashed bg-gray-50 p-4 text-center"
          >
            <p className="text-sm text-gray-500">
              No discussion points yet. {canEdit && 'Click below to add your first point.'}
            </p>
          </motion.div>
        )}
        
        {points.map((point, index) => (
          <motion.div
            key={point.id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ delay: index * 0.05 }}
            className="group relative rounded-lg border bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <Badge
                variant={point.addedBy === 'employee' ? 'default' : 'secondary'}
                className={`text-xs ${
                  point.addedBy === 'employee'
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-100 border-purple-200'
                }`}
              >
                {point.addedBy === 'employee' ? (
                  <User className="mr-1 h-3 w-3" />
                ) : (
                  <Briefcase className="mr-1 h-3 w-3" />
                )}
                {point.addedBy === 'employee' ? 'Team Member' : 'People Leader'}
              </Badge>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{getTimeAgo(point.timestamp)}</span>
                {/* Always shown on your own points, not only on hover: hidden
                    until the pointer was over the card, a Team Member couldn't
                    find Edit, and a touch screen never showed it (HR UAT #4). */}
                {canEdit && point.addedBy === currentUserRole && onUpdatePoint && editingId !== point.id && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Edit point"
                    className="h-7 w-7 text-gray-400 transition-colors hover:text-gray-700"
                    onClick={() => startEdit(point)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {canEdit && point.addedBy === currentUserRole && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Delete point"
                    className="h-7 w-7 text-gray-400 transition-colors hover:text-red-600"
                    onClick={() => onDeletePoint(point.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {editingId === point.id && canEdit ? (
              <div className="space-y-2">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[80px] resize-none"
                  maxLength={maxLength}
                  aria-label="Edit discussion point"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!editContent.trim() || editContent.length > maxLength}
                    onClick={() => saveEdit(point)}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-700 leading-relaxed">{point.content}</p>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add New Point Form */}
      {canEdit && (
        <motion.div
          initial={false}
          animate={{ height: 'auto' }}
          className="rounded-lg border bg-gray-50 p-3"
        >
          {isExpanded ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={placeholder}
                className="min-h-[80px] resize-none"
                maxLength={maxLength}
                autoFocus
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {content.length}/{maxLength}
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsExpanded(false)
                      setContent('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!content.trim() || content.length > maxLength}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Point
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start text-gray-600 hover:text-gray-900"
              onClick={() => setIsExpanded(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Discussion Point
            </Button>
          )}
        </motion.div>
      )}
    </div>
  )
}
